import { useState } from "react";
import { X } from "lucide-react";
import { Button, Input, Tag } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { WHATSAPP_PURPOSES } from "../types/whatsapp.types";
import {
  changeParams,
  datesValid,
  readDates,
} from "../services/search-params";
import { DatePresets } from "./DatePresets";
import { SearchSelect } from "@/shared/rd";
import { WaField } from "./WaField";
import { errorText, filterForm, muted, summaryStyle } from "./styles";
const copy = keys.whatsapp;
export function LedgerFilters({
  tab,
  params,
  onChange,
}: {
  tab: "usage" | "messages";
  params: URLSearchParams;
  onChange: (params: URLSearchParams) => void;
}) {
  const t = useTranslations();
  const [error, setError] = useState(false);
  const [idError, setIdError] = useState(false);
  const [groupBy, setGroupBy] = useState(
    params.get("groupBy") === "store" ? "store" : "seller",
  );
  const [acceptance, setAcceptance] = useState(params.get("acceptance") ?? "");
  const [status, setStatus] = useState(params.get("status") ?? "");
  const [purpose, setPurpose] = useState(params.get("purpose") ?? "");
  const dates = readDates(params);
  const extraKeys = [
    "acceptance",
    "status",
    "purpose",
    "template",
    "messageId",
  ];
  const activeCount = extraKeys.filter((key) => params.get(key)).length;
  const [expanded, setExpanded] = useState(
    activeCount > 0 || !datesValid(dates.dateFrom, dates.dateTo),
  );
  const scoped =
    params.has("sellerId") ||
    params.has("storeId") ||
    params.get("unattributed") === "true";
  const options = (values: string[], labels: Record<string, string>) => [
    { value: "", label: t(copy.all) },
    ...values.map((value) => ({ value, label: t(labels[value]) })),
  ];
  return (
    <form
      style={filterForm}
      onSubmit={(event) => {
        event.preventDefault();
        const values = new FormData(event.currentTarget);
        const dateFrom = String(values.get("dateFrom") ?? "");
        const dateTo = String(values.get("dateTo") ?? "");
        const messageId = String(values.get("messageId") ?? "").trim();
        const invalidDates = !datesValid(dateFrom, dateTo);
        const invalidId = Boolean(
          messageId && !/^wamid\.[A-Za-z0-9_+/=-]{1,506}$/.test(messageId),
        );
        setError(invalidDates);
        setIdError(invalidId);
        if (invalidDates || invalidId) {
          setExpanded(true);
          return;
        }
        onChange(
          changeParams(params, {
            dateFrom,
            dateTo,
            messageId,
            acceptance,
            status,
            purpose,
            template: String(values.get("template") ?? "").trim(),
            message: null,
            ...(tab === "usage"
              ? { groupBy, search: String(values.get("search") ?? "").trim() }
              : {}),
          }),
        );
      }}
    >
      <DatePresets
        dateFrom={dates.dateFrom}
        dateTo={dates.dateTo}
        onRange={(range) => onChange(changeParams(params, { ...range, message: null }))}
        onCustom={() => setExpanded((open) => !open)}
      />
      {scoped && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 13 }}>
          {params.get("sellerId") && (
            <Tag tone="blue">
              {t(copy.seller)}: <bdi>{params.get("sellerName") ?? params.get("sellerId")}</bdi>
            </Tag>
          )}
          {params.get("storeId") && (
            <Tag tone="blue">
              {t(copy.store)}: <bdi>{params.get("storeName") ?? params.get("storeId")}</bdi>
            </Tag>
          )}
          {params.get("unattributed") === "true" && <Tag tone="blue">{t(copy.unattributed)}</Tag>}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon={X}
            onClick={() =>
              onChange(
                changeParams(params, {
                  sellerId: null,
                  storeId: null,
                  sellerName: null,
                  storeName: null,
                  unattributed: null,
                  message: null,
                }),
              )
            }
          >
            {t(copy.clearScope)}
          </Button>
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
        {tab === "usage" && (
          <>
            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <WaField label={t(copy.search)}>
              {(id) => (
              <Input
                id={id}
                name="search"
                maxLength={100}
                defaultValue={params.get("search") ?? ""}
              />
              )}
            </WaField>
            </div>
            <div style={{ flex: "0 1 200px", minWidth: 0 }}>
            <WaField label={t(copy.groupBy)}>
              {(id) => (
              <SearchSelect
                id={id}
                label={t(copy.groupBy)}
                emptyLabel={t(keys.common.noResults)}
                value={groupBy}
                onChange={setGroupBy}
                options={[
                  { value: "seller", label: t(copy.seller) },
                  { value: "store", label: t(copy.store) },
                ]}
              />
              )}
            </WaField>
            </div>
          </>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button type="submit" variant="primary" size="sm">
            {t(copy.apply)}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            muted
            onClick={() =>
              onChange(
                new URLSearchParams(
                  tab === "messages" ? { tab, source: "trioty" } : { tab },
                ),
              )
            }
          >
            {t(copy.reset)}
          </Button>
        </div>
      </div>
      <details
        open={expanded}
        onToggle={(event) => setExpanded(event.currentTarget.open)}
      >
        <summary style={summaryStyle}>
          {t(copy.filters)}
          {activeCount > 0 && (
            <span style={{ marginInlineStart: 8, fontWeight: 400 }}>
              {t(copy.filterCount, { count: activeCount })}
            </span>
          )}
        </summary>
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <WaField label={t(copy.from)}>
            {(id) => (
            <Input
              id={id}
              name="dateFrom"
              type="date"
              required
              defaultValue={dates.dateFrom}
            />
            )}
          </WaField>
          <WaField label={t(copy.to)}>
            {(id) => (
            <Input
              id={id}
              name="dateTo"
              type="date"
              required
              defaultValue={dates.dateTo}
            />
            )}
          </WaField>
          <WaField label={t(copy.historyRequestResult)}>
            {(id) => (
            <SearchSelect
              id={id}
              label={t(copy.historyRequestResult)}
              emptyLabel={t(keys.common.noResults)}
              value={acceptance}
              onChange={setAcceptance}
              options={options(
                ["pending", "accepted", "rejected", "unknown"],
                copy.states,
              )}
            />
            )}
          </WaField>
          <WaField label={t(copy.status)}>
            {(id) => (
            <SearchSelect
              id={id}
              label={t(copy.status)}
              emptyLabel={t(keys.common.noResults)}
              value={status}
              onChange={setStatus}
              options={options(
                [
                  "pending",
                  "accepted",
                  "unknown",
                  "sent",
                  "delivered",
                  "read",
                  "failed",
                ],
                copy.states,
              )}
            />
            )}
          </WaField>
          <WaField label={t(copy.purpose)}>
            {(id) => (
            <SearchSelect
              id={id}
              label={t(copy.purpose)}
              emptyLabel={t(keys.common.noResults)}
              value={purpose}
              onChange={setPurpose}
              options={options([...WHATSAPP_PURPOSES], copy.purposes)}
            />
            )}
          </WaField>
          <WaField label={t(copy.template)}>
            {(id) => (
            <Input
              id={id}
              name="template"
              dir="ltr"
              pattern="[a-z0-9_]{1,512}"
              maxLength={512}
              defaultValue={params.get("template") ?? ""}
            />
            )}
          </WaField>
          <WaField
            label={t(copy.providerMessageId)}
            error={idError ? t(copy.historyMessageIdError) : undefined}
          >
            {(id, errorId) => (
            <Input
              id={id}

              invalid={Boolean(errorId)}

              aria-describedby={errorId}
              name="messageId"
              dir="ltr"
              maxLength={512}
              defaultValue={params.get("messageId") ?? ""}
            />
            )}
          </WaField>
        </div>
      </details>
      <p style={muted}>{t(copy.submissionDateHint)}</p>
      {error && (
        <p role="alert" style={errorText}>
          {t(copy.dateError)}
        </p>
      )}
    </form>
  );
}
